import moment from "moment";
import type {
  UndeliverableNumber,
  UndeliverableReason
} from "views/inbox/types";
import { normalizePhoneForOptOut } from "views/inbox/utils/smsOptOut";

const REASON_LABEL: Record<UndeliverableReason, string> = {
  landline: "Landline",
  not_in_service: "Not in service",
  unreachable: "Unreachable"
};

const REASON_BRACKET: Record<UndeliverableReason, string> = {
  landline: "landline",
  not_in_service: "not in service",
  unreachable: "unreachable"
};

const humanize = (reason: string) =>
  reason.replace(/_/g, " ").replace(/^./, (char) => char.toUpperCase());

const isKnownReason = (reason: string): reason is UndeliverableReason =>
  Object.hasOwn(REASON_LABEL, reason);

const undeliverableReasonLabel = (reason: string): string =>
  isKnownReason(reason) ? REASON_LABEL[reason] : humanize(reason);

const undeliverableReasonBracket = (reason: string): string =>
  isKnownReason(reason)
    ? REASON_BRACKET[reason]
    : reason.replace(/_/g, " ").toLowerCase();

const formatMarkedDate = (markedAt: string) =>
  moment(markedAt).format("MMM D, YYYY");

export const composerBlockLabel = (reason: string): string =>
  `${undeliverableReasonLabel(reason)}: can't receive texts`;

const REASON_CLAUSE: Record<UndeliverableReason, string> = {
  landline: "it's a landline",
  not_in_service: "it's not in service",
  unreachable: "it's unreachable"
};

const undeliverableReasonClause = (reason: string): string =>
  isKnownReason(reason)
    ? REASON_CLAUSE[reason]
    : `it's ${undeliverableReasonBracket(reason)}`;

const learnedPrefix = (status: UndeliverableNumber): string =>
  status.source === "lookup"
    ? "Boom checked this number on"
    : "A text to this number failed on";

const firstName = (name: string | null | undefined): string =>
  name?.trim().split(/\s+/)[0] ?? "";

export const composerBlockTooltip = (
  status: UndeliverableNumber,
  contactName?: string | null
): string =>
  `${learnedPrefix(status)} ${formatMarkedDate(status.marked_at)}: ${undeliverableReasonClause(status.reason)}, so it can't receive text messages. Call or email ${firstName(contactName) || "this lead"} instead.`;

export const CANT_TEXT_BADGE_LABEL = "Can't text";

export const cantTextBadgeTooltip = (status: UndeliverableNumber): string =>
  `${undeliverableReasonLabel(status.reason)}. ${learnedPrefix(status)} ${formatMarkedDate(status.marked_at)}. Text messages to it are skipped. Call or email instead.`;

export const workflowSkippedNumberText = (reason: string): string =>
  `Can't receive texts (${undeliverableReasonBracket(reason)}). Boom didn't send this text.`;

export const PENDING_AUTO_RETRY_TITLE = "Phone unreachable";

export const pendingAutoRetryText = (
  autoRetryAt: string,
  now: Date = new Date()
): string => {
  const at = moment(autoRetryAt);
  if (!at.isAfter(now)) return "Boom is trying again now";
  return at.isSame(now, "day")
    ? `Boom will try again at ${at.format("h:mm A")}`
    : `Boom will try again on ${at.format("MMM D [at] h:mm A")}`;
};

export const toE164 = (phone: string | null | undefined): string => {
  const key = normalizePhoneForOptOut(phone);
  if (!key) return "";
  return key.length === 10 ? `+1${key}` : `+${key}`;
};

export const buildUndeliverableMap = (
  numbers: UndeliverableNumber[] | undefined
): Map<string, UndeliverableNumber> => {
  const map = new Map<string, UndeliverableNumber>();
  for (const item of Array.isArray(numbers) ? numbers : []) {
    const key = normalizePhoneForOptOut(item?.phone_number);
    if (key && item.reason) map.set(key, item);
  }
  return map;
};

export const findUndeliverable = (
  phone: string | null | undefined,
  map: Map<string, UndeliverableNumber>
): UndeliverableNumber | null => {
  const key = normalizePhoneForOptOut(phone);
  return key ? (map.get(key) ?? null) : null;
};
